<template>
    <div class="space-y-8">
        <div class="mb-2">
            <h1 class="text-2xl font-semibold text-gray-800">{{ $t('account.security.title') }}</h1>
            <p class="text-normal text-gray-400 mt-1">{{ $t('account.security.description') }}</p>
        </div>

        <!-- Change Password -->
        <section class="bg-white rounded-xl border border-gray-200 p-6">
            <h2 class="text-base font-semibold text-gray-800 mb-1">{{ $t('account.security.changePassword.title') }}</h2>

            <!-- Step 1: idle — request code -->
            <template v-if="pwStep === 'idle'">
                <p class="text-sm text-gray-400 mb-4 mt-1">{{ $t('account.security.changePassword.description') }}</p>
                <div class="w-56">
                    <AppButton :loading="pwSaving" variant="secondary" @click="requestPasswordCode">
                        {{ $t('account.security.changePassword.sendCode') }}
                    </AppButton>
                </div>
            </template>

            <!-- Step 2: code sent — enter code + new password -->
            <template v-else>
                <p class="text-sm text-gray-500 mb-4 mt-1">{{ $t('account.security.changePassword.codeSentInfo') }}</p>
                <form class="space-y-3" @submit.prevent="submitPasswordChange">
                    <AppInput
                        v-model="pwForm.code"
                        :label="$t('account.security.changePassword.code')"
                        autocomplete="one-time-code"
                        inputmode="numeric"
                        maxlength="6"
                        :error="pwErrors.code"
                    />
                    <AppInput
                        v-model="pwForm.newPassword"
                        :label="$t('account.security.changePassword.new')"
                        type="password"
                        autocomplete="new-password"
                        :error="pwErrors.newPassword"
                    />
                    <AppInput
                        v-model="pwForm.confirm"
                        :label="$t('account.security.changePassword.confirm')"
                        type="password"
                        autocomplete="new-password"
                        :error="pwErrors.confirm"
                    />
                    <p v-if="pwErrors.general" class="text-xs text-error-500">{{ pwErrors.general }}</p>
                    <div class="flex items-center justify-between pt-1">
                        <button
                            type="button"
                            class="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-40 transition-colors"
                            :disabled="resendCooldown > 0"
                            @click="requestPasswordCode"
                        >
                            {{ resendCooldown > 0 ? $t('account.security.changePassword.resendIn', { s: resendCooldown }) : $t('account.security.changePassword.resend') }}
                        </button>
                        <div class="flex gap-2">
                            <div class="w-20">
                                <AppButton variant="white" @click="cancelPasswordChange">
                                    {{ $t('account.security.changePassword.cancel') }}
                                </AppButton>
                            </div>
                            <div class="w-44">
                                <AppButton :loading="pwSaving" @click="submitPasswordChange">
                                    {{ $t('account.security.changePassword.submit') }}
                                </AppButton>
                            </div>
                        </div>
                    </div>
                </form>
            </template>
        </section>

        <!-- Two-Factor Authentication -->
        <section class="bg-white rounded-xl border border-gray-200 p-6">
            <h2 class="text-base font-semibold text-gray-800 mb-1">{{ $t('account.security.twoFactor.title') }}</h2>
            <p class="text-sm text-gray-400 mt-1 mb-4">{{ $t('account.security.twoFactor.notAvailable') }}</p>
            <div class="w-44">
                <AppButton variant="secondary" :disabled="true">
                    {{ $t('account.security.twoFactor.enable') }}
                </AppButton>
            </div>
        </section>

        <!-- Active Sessions -->
        <section class="bg-white rounded-xl border border-gray-200 p-6">
            <div class="flex items-start justify-between mb-5">
                <div>
                    <h2 class="text-base font-semibold text-gray-800">{{ $t('account.security.sessions.title') }}</h2>
                    <p class="text-sm text-gray-400 mt-0.5">{{ $t('account.security.sessions.description') }}</p>
                </div>
                <button
                    v-if="sessions.length > 1"
                    class="text-sm font-medium text-error-500 hover:text-error-600 transition-colors flex-shrink-0 mt-0.5"
                    @click="revokeAll"
                >
                    {{ $t('account.security.sessions.revokeAll') }}
                </button>
            </div>

            <!-- Loading skeletons -->
            <div v-if="sessionsLoading" class="space-y-3">
                <div v-for="i in 2" :key="i" class="animate-pulse rounded-xl border border-gray-100 p-4 flex items-center gap-4">
                    <div class="w-10 h-10 bg-gray-100 rounded-full flex-shrink-0"></div>
                    <div class="flex-1 space-y-2">
                        <div class="h-3.5 bg-gray-100 rounded w-40"></div>
                        <div class="h-3 bg-gray-100 rounded w-52"></div>
                        <div class="h-3 bg-gray-100 rounded w-32"></div>
                    </div>
                </div>
            </div>

            <!-- Empty -->
            <div v-else-if="sessions.length === 0" class="text-sm text-gray-400 py-1">
                {{ $t('account.security.sessions.empty') }}
            </div>

            <!-- Session cards -->
            <transition-group v-else name="session-list" tag="ul" class="space-y-3">
                <li
                    v-for="session in sessions"
                    :key="session.id"
                    class="rounded-xl border p-4 flex items-center gap-4 transition-colors duration-150"
                    :class="session.isCurrent ? 'border-success-200 bg-success-50' : 'border-gray-100 bg-gray-50 hover:bg-gray-100'"
                >
                    <!-- Device icon -->
                    <div
                        class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        :class="session.isCurrent ? 'bg-success-100 text-success-600' : 'bg-white border border-gray-200 text-gray-500'"
                    >
                        <component :is="getDeviceIcon(session.device)" class="w-[18px] h-[18px]" />
                    </div>

                    <!-- Info -->
                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2 flex-wrap">
                            <span class="text-sm font-semibold text-gray-800 truncate">
                                {{ sessionLabel(session) }}
                            </span>
                            <span
                                v-if="session.isCurrent"
                                class="inline-flex items-center gap-1.5 text-xs font-medium text-success-700 bg-success-100 px-2 py-0.5 rounded-full flex-shrink-0"
                            >
                                <span class="w-1.5 h-1.5 rounded-full bg-success-500 inline-block animate-pulse"></span>
                                {{ $t('account.security.sessions.current') }}
                            </span>
                        </div>
                        <div v-if="getLocationText(session)" class="flex items-center gap-1 mt-0.5 text-xs text-gray-500">
                            <MapPin class="w-3 h-3 flex-shrink-0" />
                            <span>{{ getLocationText(session) }}</span>
                        </div>
                        <div class="flex items-center gap-2 mt-0.5 text-xs text-gray-400">
                            <span class="font-mono">{{ session.ip }}</span>
                            <span aria-hidden="true">·</span>
                            <span>{{ formatRelative(session.lastActive) }}</span>
                        </div>
                    </div>

                    <!-- Map thumbnail -->
                    <img
                        v-if="getMapUrl(session)"
                        :src="getMapUrl(session)!"
                        alt=""
                        class="w-28 h-16 rounded-lg object-cover flex-shrink-0 opacity-80"
                        @error="($event.target as HTMLImageElement).style.display = 'none'"
                    />

                    <!-- Revoke -->
                    <button
                        v-if="!session.isCurrent"
                        class="text-xs font-medium text-error-500 hover:text-error-700 hover:bg-error-50 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0 border border-transparent hover:border-error-100"
                        @click="revokeSession(session.id)"
                    >
                        {{ $t('account.security.sessions.revokeOne') }}
                    </button>
                </li>
            </transition-group>
        </section>
    </div>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted, onUnmounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow } from 'date-fns';
import { dateFnsLocale } from '@/plugins/i18n/dateFns';
import { Laptop, Smartphone, Monitor, MapPin } from 'lucide-vue-next';
import AppInput from '@/components/ui/AppInput.vue';
import AppButton from '@/components/ui/AppButton.vue';
import { securityApi } from '@/api/security';
import type { Session } from '@/types';

const toast = useToast();
const { t, locale } = useI18n();

// ── Change Password ──────────────────────────────────────────────
const pwStep = ref<'idle' | 'code_sent'>('idle');
const pwForm = reactive({ code: '', newPassword: '', confirm: '' });
const pwErrors = reactive({ code: '', newPassword: '', confirm: '', general: '' });
const pwSaving = ref(false);
const resendCooldown = ref(0);
let resendTimer: ReturnType<typeof setInterval> | null = null;

function startResendCooldown() {
    resendCooldown.value = 60;
    resendTimer = setInterval(() => {
        resendCooldown.value--;
        if (resendCooldown.value <= 0 && resendTimer) {
            clearInterval(resendTimer);
            resendTimer = null;
        }
    }, 1000);
}

async function requestPasswordCode() {
    pwSaving.value = true;
    try {
        await securityApi.requestPasswordChangeCode();
        pwStep.value = 'code_sent';
        pwForm.code = '';
        pwErrors.code = '';
        pwErrors.general = '';
        startResendCooldown();
        toast.success(t('account.security.changePassword.codeSent'));
    } catch {
        toast.error(t('errors.unknown'));
    } finally {
        pwSaving.value = false;
    }
}

function cancelPasswordChange() {
    pwStep.value = 'idle';
    pwForm.code = '';
    pwForm.newPassword = '';
    pwForm.confirm = '';
    pwErrors.code = '';
    pwErrors.newPassword = '';
    pwErrors.confirm = '';
    pwErrors.general = '';
    if (resendTimer) { clearInterval(resendTimer); resendTimer = null; }
    resendCooldown.value = 0;
}

async function submitPasswordChange() {
    pwErrors.code = '';
    pwErrors.newPassword = '';
    pwErrors.confirm = '';
    pwErrors.general = '';

    if (!pwForm.code) { pwErrors.code = t('account.security.changePassword.required'); return; }
    if (!pwForm.newPassword) { pwErrors.newPassword = t('account.security.changePassword.required'); return; }
    if (pwForm.newPassword.length < 8) { pwErrors.newPassword = t('account.security.changePassword.tooShort'); return; }
    if (pwForm.newPassword !== pwForm.confirm) { pwErrors.confirm = t('account.security.changePassword.mismatch'); return; }

    pwSaving.value = true;
    try {
        await securityApi.changePassword(pwForm.code, pwForm.newPassword);
        cancelPasswordChange();
        toast.success(t('account.security.changePassword.success'));
    } catch (err: any) {
        const status = err?.response?.status;
        const errorCode = err?.response?.data?.error;
        if (status === 400 && errorCode === 'invalid_code') {
            pwErrors.code = t('account.security.changePassword.invalidCode');
        } else if (status === 422 && errorCode === 'password_too_short') {
            pwErrors.newPassword = t('account.security.changePassword.tooShort');
        } else {
            pwErrors.general = t('errors.unknown');
        }
    } finally {
        pwSaving.value = false;
    }
}

onMounted(async () => {
    await loadSessions();
});

onUnmounted(() => {
    if (resendTimer) clearInterval(resendTimer);
});

// ── Active Sessions ──────────────────────────────────────────────
const sessions = ref<Session[]>([]);
const sessionsLoading = ref(true);

// Geo enrichment
type GeoEntry = { city: string; country: string; countryCode: string; lat: number; lon: number };
const geoCache = new Map<string, GeoEntry>();
const geoMap = ref(new Map<string, GeoEntry>());

function isPrivateIp(ip: string): boolean {
    return (
        !ip ||
        ip === '::1' ||
        ip.startsWith('127.') ||
        ip.startsWith('192.168.') ||
        ip.startsWith('10.') ||
        /^172\.(1[6-9]|2\d|3[01])\./.test(ip)
    );
}

async function fetchGeo(ip: string): Promise<void> {
    if (isPrivateIp(ip) || geoCache.has(ip)) return;
    try {
        const res = await fetch(`https://ipapi.co/${ip}/json/`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.city && data.country_name && data.country_code) {
            geoCache.set(ip, {
                city: data.city,
                country: data.country_name,
                countryCode: data.country_code,
                lat: data.latitude,
                lon: data.longitude,
            });
            geoMap.value = new Map(geoCache);
        }
    } catch { /* geo is non-critical */ }
}

function countryFlag(code: string): string {
    return [...code.toUpperCase()].map(c => String.fromCodePoint(c.charCodeAt(0) + 0x1F1E6 - 65)).join('');
}

function getLocationText(session: Session): string {
    if (isPrivateIp(session.ip)) return t('account.security.sessions.localNetwork');
    const geo = geoMap.value.get(session.ip);
    if (!geo) return ''; // still resolving — don't duplicate the IP
    return `${geo.city}, ${geo.country} ${countryFlag(geo.countryCode)}`;
}

function getMapUrl(session: Session): string | null {
    const geo = geoMap.value.get(session.ip);
    if (!geo?.lat || !geo?.lon) return null;
    return `https://staticmap.openstreetmap.de/staticmap.php?center=${geo.lat},${geo.lon}&zoom=10&size=220x120&markers=${geo.lat},${geo.lon},lightblue`;
}

function getDeviceIcon(device: string) {
    const d = (device ?? '').toLowerCase();
    if (d.includes('mobile') || d.includes('android') || d.includes('iphone')) return Smartphone;
    if (d.includes('windows') || d.includes('mac') || d.includes('linux')) return Laptop;
    return Monitor;
}

function sessionLabel(session: Session): string {
    const { browser, device } = session;
    if (browser && device) {
        return t('account.security.sessions.deviceLabel', { browser, device });
    }
    return browser || device || t('account.security.sessions.unknownDevice');
}

function formatRelative(dateStr: string): string {
    try {
        return formatDistanceToNow(new Date(dateStr), {
            addSuffix: true,
            locale: dateFnsLocale(locale.value),
        });
    } catch {
        return dateStr;
    }
}

async function loadSessions() {
    sessionsLoading.value = true;
    try {
        sessions.value = await securityApi.getSessions();
        const uniqueIps = [...new Set(sessions.value.map(s => s.ip).filter(ip => ip && !isPrivateIp(ip)))];
        await Promise.allSettled(uniqueIps.map(fetchGeo));
    } catch {
        // Non-fatal
    } finally {
        sessionsLoading.value = false;
    }
}

async function revokeSession(id: string) {
    try {
        await securityApi.revokeSession(id);
        sessions.value = sessions.value.filter((s) => s.id !== id);
        toast.success(t('account.security.sessions.revokeSuccess'));
    } catch {
        toast.error(t('errors.unknown'));
    }
}

async function revokeAll() {
    try {
        await securityApi.revokeAllSessions();
        sessions.value = sessions.value.filter((s) => s.isCurrent);
        toast.success(t('account.security.sessions.revokeAllSuccess'));
    } catch {
        toast.error(t('errors.unknown'));
    }
}
</script>

<style scoped>
.session-list-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}
.session-list-leave-to {
    opacity: 0;
    transform: translateX(6px);
}
</style>
